import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, AlertCircle, AlertTriangle, XCircle, Clock, Snowflake,
  ChevronRight, ChevronDown, RotateCcw, X, Layers, Link2
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import config from '@/config';

// Debugging stuck work.
//
// Failures are grouped by stage + error rather than listed one by one: the
// useful fact is "one bug hit 1,905 nodes, plus 200 one-offs", which a flat
// list of 2,132 rows hides completely.

const fmt = (n) => (n == null ? '—' : n.toLocaleString());

const shortDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(v).slice(0, 16); }
};

const idleLabel = (hours) => {
  if (hours == null) return '—';
  if (hours < 48) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
};

function ResetConfirm({ request, label, onCancel, onDone, authenticatedFetch }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/nodes/debug/reset/preview`,
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request) }
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [request, authenticatedFetch]);

  const apply = async () => {
    setApplying(true); setError(null);
    try {
      const res = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/debug/reset/apply`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request) }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      onDone(data);
    } catch (err) { setError(err.message); setApplying(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="px-5 py-4 border-b border-gray-200 flex items-start gap-3">
          <RotateCcw className="w-5 h-5 text-gray-700 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Reset to pending</h3>
            <p className="text-xs text-gray-500 mt-0.5">{label}</p>
          </div>
        </div>

        <div className="px-5 py-4 space-y-3">
          {loading ? (
            <div className="py-6 flex items-center justify-center gap-2 text-gray-500">
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span className="text-sm">Checking what this affects…</span>
            </div>
          ) : error ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-900">{error}</div>
          ) : preview ? (
            <>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                <div className="text-sm font-medium text-amber-900">
                  {fmt(preview.count)} {preview.count === 1 ? 'node' : 'nodes'} back to pending
                </div>
                <div className="mt-2 space-y-0.5">
                  {Object.entries(preview.by_stage).map(([stage, n]) => (
                    <div key={stage} className="text-xs text-amber-800">{stage}: {fmt(n)}</div>
                  ))}
                </div>
              </div>
              <p className="text-xs text-gray-600">
                {preview.clear_output
                  ? 'Previous output is cleared, so a retried node no longer looks broken and nothing downstream reads a stale result. Any lock is dropped.'
                  : 'Previous output is kept.'}
              </p>
              <p className="text-xs text-gray-500">
                The workers pick these up again on their own. If the underlying cause
                is still there, they will fail the same way.
              </p>
            </>
          ) : null}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex gap-3">
          <button onClick={onCancel} disabled={applying}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={apply} disabled={loading || applying || !!error || !preview?.count}
            className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-40 flex items-center justify-center gap-2">
            {applying ? (<><RefreshCw className="w-4 h-4 animate-spin" />Resetting…</>) : 'Confirm reset'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NodeDetail({ nodeId, onClose, authenticatedFetch }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedFetch(`${config.BACKEND_API_ADDRESS}/nodes/debug/node/${nodeId}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
        if (!cancelled) setDetail(data);
      } catch (err) { if (!cancelled) setError(err.message); }
    })();
    return () => { cancelled = true; };
  }, [nodeId, authenticatedFetch]);

  const statusPill = (status) => {
    const tone = status === 'failed' ? 'bg-red-50 text-red-800 border-red-200'
      : status === 'completed' ? 'bg-green-50 text-green-800 border-green-200'
      : status === 'running' ? 'bg-amber-50 text-amber-900 border-amber-200'
      : 'bg-gray-100 text-gray-700 border-gray-200';
    return <span className={`px-1.5 py-0.5 rounded border text-[11px] font-medium ${tone}`}>{status}</span>;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[65] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Node detail</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full">
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-4">
          {error ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-900">{error}</div>
          ) : !detail ? (
            <div className="py-8 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : (
            <>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-gray-900">{detail.node.stage}</span>
                  {statusPill(detail.node.status)}
                  <span className="text-xs text-gray-500">idle {idleLabel(detail.node.hours_idle)}</span>
                </div>
                <div className="mt-1 text-xs text-gray-500 font-mono break-all">{detail.node.node_id}</div>
                {detail.node.workflow_name && (
                  <div className="mt-1 text-xs text-gray-600">
                    workflow: <span className="font-mono">{detail.node.workflow_name}</span>
                  </div>
                )}
              </div>

              {detail.node.error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <div className="text-xs font-semibold text-red-900 uppercase tracking-wide mb-1">Error</div>
                  <div className="text-sm text-red-900 break-words">{detail.node.error}</div>
                </div>
              )}

              {detail.blocking_dependencies?.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                  <div className="text-xs font-semibold text-amber-900 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <Link2 className="w-3.5 h-3.5" />
                    Blocked by upstream
                  </div>
                  {detail.blocking_dependencies.map((d) => (
                    <div key={d.node_id} className="text-xs text-amber-900 flex items-center gap-2">
                      {statusPill(d.status)} {d.stage}
                      {d.error && <span className="opacity-80 truncate">— {d.error}</span>}
                    </div>
                  ))}
                  <p className="mt-2 text-[11px] text-amber-800">
                    Fixing this node alone will not help; the upstream one has to succeed first.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {[['Dependencies', detail.dependencies], ['Dependents', detail.dependents]].map(([title, list]) => (
                  <div key={title}>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      {title} ({list.length})
                    </div>
                    {list.length === 0 ? (
                      <div className="text-xs text-gray-400">none</div>
                    ) : (
                      <div className="space-y-1">
                        {list.map((d) => (
                          <div key={d.node_id} className="flex items-center gap-2 text-xs text-gray-700">
                            {statusPill(d.status)}
                            <span className="truncate">{d.stage}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {detail.node.output_data && (
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">output_data</div>
                  <pre className="text-[11px] bg-gray-50 border border-gray-200 rounded p-3 overflow-x-auto max-h-48">
                    {JSON.stringify(detail.node.output_data, null, 2)}
                  </pre>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function WorkflowDebug() {
  const { authenticatedFetch } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [staleHours, setStaleHours] = useState(2);
  const [expanded, setExpanded] = useState(null);
  const [resetRequest, setResetRequest] = useState(null);
  const [resetLabel, setResetLabel] = useState('');
  const [result, setResult] = useState(null);
  const [detailNode, setDetailNode] = useState(null);

  const fetchOverview = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/debug/overview?stale_hours=${staleHours}`
      );
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || `HTTP ${res.status}`);
      setData(body);
    } catch (err) {
      console.error('Error loading debug overview:', err);
      setError(err.message);
    } finally { setLoading(false); }
  }, [authenticatedFetch, staleHours]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const askReset = (request, label) => { setResetRequest(request); setResetLabel(label); };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Workflow Debug</h1>
          <p className="text-sm text-gray-600 mt-1">
            Work that has stopped moving — failures grouped by cause, and nodes left running.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-600">Stuck after</label>
          <select
            value={staleHours}
            onChange={(e) => setStaleHours(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
          >
            <option value={0.5}>30 min</option>
            <option value={2}>2 hours</option>
            <option value={12}>12 hours</option>
            <option value={24}>24 hours</option>
          </select>
          <button onClick={fetchOverview} disabled={loading}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-900">{error}</div>
        </div>
      )}

      {loading && !data ? (
        <div className="py-20 flex flex-col items-center gap-3 text-gray-500">
          <RefreshCw className="w-8 h-8 animate-spin" />
          <span className="text-sm">Scanning for stuck work…</span>
        </div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4" style={{ color: '#d03b3b' }} />
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Failed</span>
              </div>
              <div className="mt-2 text-2xl font-semibold text-gray-900">{fmt(data.failed.total)}</div>
              <div className="mt-0.5 text-xs text-gray-500">
                {fmt(data.failed.distinct_problems)} distinct{' '}
                {data.failed.distinct_problems === 1 ? 'problem' : 'problems'}
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" style={{ color: '#fab219' }} />
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Stuck running</span>
              </div>
              <div className="mt-2 text-2xl font-semibold text-gray-900">{fmt(data.stuck_running.total)}</div>
              <div className="mt-0.5 text-xs text-gray-500">
                of {fmt(data.stuck_running.running_total)} running · idle &gt; {staleHours}h
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2">
                <Snowflake className="w-4 h-4 text-gray-500" />
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Frozen</span>
              </div>
              <div className="mt-2 text-2xl font-semibold text-gray-900">{fmt(data.frozen.total)}</div>
              <div className="mt-0.5 text-xs text-gray-500">
                {Object.keys(data.frozen.by_stage).join(', ') || 'none'} · not touched here
              </div>
            </div>
          </div>

          {/* Failures, grouped by what went wrong */}
          <div className="bg-white rounded-lg border border-gray-200 mb-6 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-baseline justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Failures by cause</h2>
              <span className="text-xs text-gray-500">largest first</span>
            </div>
            {data.failed.groups.length === 0 ? (
              <div className="px-5 py-8 text-sm text-gray-500 text-center">No failures.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {data.failed.groups.slice(0, 40).map((g, i) => {
                  const key = `${g.workflow_item_id}:${g.error}`;
                  const open = expanded === key;
                  return (
                    <div key={key}>
                      <div className="px-5 py-3 flex items-center gap-3 hover:bg-gray-50">
                        <button onClick={() => setExpanded(open ? null : key)} className="flex-1 flex items-center gap-3 text-left min-w-0">
                          {open ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                          <span className="text-sm font-semibold text-gray-900 tabular-nums w-16 flex-shrink-0">
                            {fmt(g.count)}×
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 flex-shrink-0">
                            {g.stage}
                          </span>
                          <span className="text-sm text-gray-700 truncate" title={g.error}>{g.error}</span>
                        </button>
                        <button
                          onClick={() => askReset(
                            { status: 'failed', workflow_item_id: g.workflow_item_id, error: g.error, clear_output: true },
                            `${g.stage} — ${g.error}`
                          )}
                          className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5 flex-shrink-0"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                          Retry {fmt(g.count)}
                        </button>
                      </div>
                      {open && (
                        <div className="px-5 pb-4 pl-16 bg-gray-50">
                          <div className="text-xs text-gray-500 mb-2">
                            first {shortDate(g.first_seen)} · last {shortDate(g.last_seen)}
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {g.sample_node_ids.map((id) => (
                              <button key={id} onClick={() => setDetailNode(id)}
                                className="px-2 py-1 text-[11px] font-mono bg-white border border-gray-200 rounded hover:border-gray-400">
                                {id.slice(0, 8)}
                              </button>
                            ))}
                            {g.count > g.sample_node_ids.length && (
                              <span className="px-2 py-1 text-[11px] text-gray-500">
                                +{fmt(g.count - g.sample_node_ids.length)} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {data.failed.groups.length > 40 && (
                  <div className="px-5 py-3 text-xs text-gray-500">
                    Showing the 40 largest of {fmt(data.failed.groups.length)} groups.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Left running with nothing working on them */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Stuck running</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Left in <span className="font-mono">running</span> with no progress for over {staleHours}h.
                </p>
              </div>
              {data.stuck_running.total > 0 && (
                <button
                  onClick={() => askReset(
                    { status: 'running', stale_hours: staleHours, clear_output: true },
                    `all nodes running with no progress for over ${staleHours}h`
                  )}
                  className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset all {fmt(data.stuck_running.total)}
                </button>
              )}
            </div>
            {data.stuck_running.nodes.length === 0 ? (
              <div className="px-5 py-8 text-sm text-gray-500 text-center">Nothing stuck.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-5 py-2 font-medium">Stage</th>
                    <th className="text-right px-3 py-2 font-medium">Idle</th>
                    <th className="text-left px-3 py-2 font-medium">Lock</th>
                    <th className="text-left px-3 py-2 font-medium">Last update</th>
                    <th className="px-5 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.stuck_running.nodes.map((n) => (
                    <tr key={n.node_id} className="hover:bg-gray-50">
                      <td className="px-5 py-2 text-gray-900 flex items-center gap-2">
                        <Layers className="w-3.5 h-3.5 text-gray-400" />
                        {n.stage}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-gray-900">
                        {idleLabel(n.hours_idle)}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {n.lock_state === 'held' ? (
                          <span className="text-amber-800">held — someone may be working</span>
                        ) : n.lock_state === 'expired' ? 'expired' : 'none'}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">{shortDate(n.updated_at)}</td>
                      <td className="px-5 py-2 text-right">
                        <button onClick={() => setDetailNode(n.node_id)}
                          className="text-xs text-gray-600 hover:text-gray-900 underline">
                          inspect
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {data.frozen.total > 0 && (
            <p className="mt-3 text-xs text-gray-500 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              {fmt(data.frozen.total)} frozen nodes are shown for awareness only. Nothing in this
              codebase sets that status, so it is left alone.
            </p>
          )}
        </>
      )}

      {resetRequest && (
        <ResetConfirm
          request={resetRequest}
          label={resetLabel}
          authenticatedFetch={authenticatedFetch}
          onCancel={() => setResetRequest(null)}
          onDone={async (res) => { setResetRequest(null); setResult(res); await fetchOverview(); }}
        />
      )}

      {detailNode && (
        <NodeDetail nodeId={detailNode} onClose={() => setDetailNode(null)} authenticatedFetch={authenticatedFetch} />
      )}

      {result && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Reset applied</h3>
            <div className="space-y-1 text-sm text-gray-700">
              <div>{fmt(result.reset)} nodes back to pending</div>
              {result.locks_cleared > 0 && <div>{fmt(result.locks_cleared)} locks cleared</div>}
              {result.output_cleared && <div>previous output cleared</div>}
            </div>
            <button onClick={() => setResult(null)}
              className="mt-5 w-full px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkflowDebug;
